import type { EmbedProviderType } from "./integration.interface";

export interface Tenant {
    id: number;
    name: string;
    displayName: string;
    subdomain: string;
    tenantId: string | null;
    status: string;
    type: string;
    // Question-packet licensing (v2.0.11)
    questionLicensingEnabled?: boolean;
    questionLimit?: number | null;
    questionCountStartDate?: string | null;
    questionsUsed?: number;
    // Admin feature permissions: SPARSE overrides of the catalog defaults
    // (null/absent = all defaults). Resolve against the fetched catalog.
    adminPermissions?: Record<string, boolean> | null;
    // Web site crawl ceilings: SPARSE overrides of the environment defaults
    // (web_scraper_provider_design.md §7.4); null/absent = all defaults.
    crawlLimits?: Record<string, number> | null;
}

/** GET /admin/tenants/:companyId/crawl-limits */
export interface TenantCrawlLimits {
    defaults: Record<string, number>;
    overrides: Record<string, number> | null;
    effective: Record<string, number>;
}

/** One row of the admin-permission catalog (served by the backend registry). */
export interface AdminPermissionMeta {
    key: string;
    label: string;
    group: string;
    description: string;
    default: boolean;
    /** Per-user permission (tenant_auth_delegation_design.md): also needs a grant per admin. */
    perUser?: boolean;
}

/** A tenant's company admin with their per-user grants (GET /:companyId/admins). */
export interface TenantAdmin {
    uid: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    grants: Record<string, boolean>;
}

/** Sign-in methods a tenant can switch on or off (backend tenantAuthSettings.ts). */
export type SignInMethod = "password" | "google" | "microsoft" | "sso";

export const SIGN_IN_METHOD_LABELS: Record<SignInMethod, string> = {
    password: "Email and password",
    google: "Google",
    microsoft: "Microsoft",
    sso: "SSO (SAML / OIDC)"
};

/** Rules for the email-and-password method (Phase 3). Defaults = Firebase's own 6-character rule, never expires. */
export interface PasswordPolicy {
    /** 6–30 */
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumeric: boolean;
    requireSymbol: boolean;
    /** Days before a password must be changed; null = never. */
    expireDays: number | null;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
    minLength: 6,
    requireUppercase: false,
    requireLowercase: false,
    requireNumeric: false,
    requireSymbol: false,
    expireDays: null
};

/** Resolved tenant authentication settings (tenant_authentication_plan.md §2.1). */
export interface TenantAuthSettings {
    methods: Record<SignInMethod, boolean>;
    allowSupportAccess: boolean;
    /** Non-empty = only these email domains may sign in (lower-case, no @). */
    allowedDomains: string[];
    /** Entra directory ids Microsoft sign-in is pinned to while a domain list is set (D11). */
    microsoftDirectoryIds: string[];
    passwordPolicy: PasswordPolicy;
    /** Phase 5: create users at first login (needs a domain list + default Assistants). */
    autoProvision: { enabled: boolean };
}

/** One row of the Default Assistants list (Phase 5); the granted role is always "user". */
export interface TenantDefaultAssistant {
    knowledgeBaseUid: string;
    name: string;
    displayName: string;
    hidden: boolean;
    position: number;
}

/** A tenant Assistant the default-Assistants picker can offer. */
export interface TenantAssistantOption {
    uid: string;
    name: string;
    displayName: string;
    hidden: boolean;
}

/** D3: who a proposed allowed-domains list would lock out. */
export interface TenantDomainImpact {
    domains: string[];
    count: number;
    users: { email: string; firstName: string | null; lastName: string | null; role: string }[];
    pendingInvites: number;
}

/**
 * A main-app SSO provider (Phase 4): one SAML/OIDC identity provider offered on
 * the tenant's login page. IdP details live only in Firebase and are never
 * returned; this is what the Authentication tab shows and gates on.
 */
export interface TenantAuthProvider {
    id: string;
    companyId: number;
    providerType: EmbedProviderType;
    /** `saml.c<companyId>-<id>`; null until the IdP details were saved. */
    firebaseProviderId: string | null;
    firebaseTenantId: string | null;
    displayName: string;
    isEnabled: boolean;
    providerConfiguredAt: string | null;
    lastSsoTestAt: string | null;
    lastSsoTestOutcome: "passed" | "failed" | null;
    createdAt: string;
    updatedAt: string;
}

/** What the login page gets per enabled SSO provider. */
export interface SsoProviderOption {
    id: string;
    providerType: EmbedProviderType;
    firebaseProviderId: string;
    displayName: string;
}

/** What the login page needs before anyone signs in. */
export interface TenantAuthOptions {
    methods: Record<SignInMethod, boolean>;
    passwordPolicy: PasswordPolicy;
    /** Enabled SSO providers; empty unless methods.sso is on. */
    ssoProviders: SsoProviderOption[];
    /** Phase 5: the sign-up page works without an invite. */
    selfRegistration: boolean;
}

export interface TenantLicensing {
    enabled: boolean;
    limit: number | null;
    startDate: string | null;
    used: number;
}
